import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChatMessage from '../components/chat/ChatMessage';
import Sidebar from '../components/layout/Sidebar';
import NewChatButton from '../components/chat/NewChatButton';
import { getCurrentReport, isTodayReportViewed } from '../utils/reportGenerator';
import { Chat as ChatType } from '../types/chat';
import {
  sendChatMessage,
  fetchPdfBinary,
  fetchChatSessions,
  convertSessionToChat,
  deleteChatSession,
  transcribeAudio
} from '../utils/chatApi';
import { v4 as uuidv4 } from 'uuid';

// Define file attachment type
interface FileAttachment {
  name: string;
  type: string;
  size: number;
  url?: string;
}

// Define Message type with file attachments
interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  sources?: {
    id: string;
    title: string;
    page: number;
    content: string;
    type: 'pdf' | 'doc' | 'text';
    url?: string;
  }[];
  files?: FileAttachment[];
}

// Update the Chat type to include sessionId
// This is a temporary update; you may want to properly update the types file
/**
 * Extended Chat type that includes sessionId.
 *
 * The sessionId is used to maintain conversation context with the Agent GG API.
 * Each chat has a unique sessionId that is passed to the API with each message,
 * allowing the API to maintain context and conversation history.
 */
interface ExtendedChat extends ChatType {
  sessionId?: string;
  messages: Message[];
}

const Chat = () => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [chats, setChats] = useState<ExtendedChat[]>([]);
  const [currentChat, setCurrentChat] = useState<ExtendedChat | null>(null);
  const [showReportNotification, setShowReportNotification] = useState(false);
  const [showCompetitionAlerts, setShowCompetitionAlerts] = useState(true);
  const [useKnowledgeBase, setUseKnowledgeBase] = useState(true);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialLoadDone = useRef(false);
  const autoCreatedChatRef = useRef(false); // Ref to track auto-created chats
  const [toast, setToast] = useState<{
    visible: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    visible: false,
    message: '',
    type: 'success'
  });
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [showButtonsDrawer, setShowButtonsDrawer] = useState(false);
  const [chatPagination, setChatPagination] = useState({
    limit: 10,
    skip: 0,
    hasMore: true
  });
  const [isRecording, setIsRecording] = useState(false);
  const [audioRecorder, setAudioRecorder] = useState<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const [fileAttachments, setFileAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Array of loading messages to cycle through
  const loadingMessages = [
    "Thinking...",
    "Calling tools...",
    "Communicating with agents...",
    "Almost there...",
    "Weighing alternatives...",
    "Considering different perspectives...",
    "Taking the time to give you the best result...",
    "Processing your request...",
    "Analyzing information...",
    "Synthesizing response...",
    "Connecting the dots...",
    "Exploring possibilities...",
    "Consulting knowledge base...",
    "Generating insights...",
    "Crafting a thoughtful response...",
    "Checking sources...",
    "Weighing alternatives...",
    "Finding the best answer for you...",
    "Just a moment longer...",
    "Putting it all together...",

    "Optimizing for accuracy..."
  ];

  // Effect to cycle through loading messages
  useEffect(() => {
    if (isGenerating) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [isGenerating]);

  // Effect to check for new reports when user logs in
  useEffect(() => {
    // Check if we should show report notification
    const checkForNewReport = () => {
      // Check if report for today has been viewed
      if (!isTodayReportViewed()) {
        setShowReportNotification(true);
      } else {
        setShowReportNotification(false);
      }
    };

    // Check when component mounts
    checkForNewReport();
  }, [user]);

  // Handle viewing the report
  const handleViewReport = () => {
    // Hide notification
    setShowReportNotification(false);

    // Navigate to report page
    navigate('/report');
  };

  // Check if latest chat has messages, create a new one if it does
  const checkAndCreateNewChatIfNeeded = (loadedChats: ExtendedChat[]) => {
    if (loadedChats.length === 0) return null;

    // Get the latest chat (first in the array)
    const latestChat = loadedChats[0];

    // Check if the latest chat has messages
    if (latestChat.messages && latestChat.messages.length > 0) {
      // Create a new chat with empty messages and a new session ID
      const newChat: ExtendedChat = {
        id: Date.now().toString(),
        title: 'New Chat',
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        sessionId: uuidv4() // Generate a new session ID
      };

      // Update chats array with the new chat at the beginning
      const updatedChats = [newChat, ...loadedChats];
      setChats(updatedChats);

      // Save updated chats to sessionStorage
      sessionStorage.setItem('chats', JSON.stringify(updatedChats));

      return newChat;
    }

    // If latest chat has no messages, just return it
    return latestChat;
  };

  useEffect(() => {
    // Only load chats from API, never from sessionStorage
    const loadChats = async (append = false) => {
      if (user) {
        setIsLoadingSessions(true);
        try {
          // Fetch sessions from the API with pagination parameters
          const sessions = await fetchChatSessions(
            chatPagination.limit,
            chatPagination.skip
          );

          // Update pagination state - if we got fewer sessions than the limit, there are no more to load
          setChatPagination(prev => ({
            ...prev,
            hasMore: sessions.length >= prev.limit
          }));

          // If the API returns an empty array or invalid data, show a message
          if (!sessions || sessions.length === 0) {
            console.log('No chat sessions found or empty response from API');

            // Create a new empty chat for the user
            const newChat: ExtendedChat = {
              id: Date.now().toString(),
              title: 'New Chat',
              messages: [],
              createdAt: Date.now(),
              updatedAt: Date.now(),
              sessionId: uuidv4() // Generate a new session ID locally
            };
            setChats([newChat]);
            return;
          }

          // Convert sessions to the ExtendedChat format
          const convertedChats = sessions.map(session => convertSessionToChat(session))
            .filter(chat => chat !== null) as ExtendedChat[];

          if (convertedChats.length === 0) {
            // If conversion resulted in no valid chats, create a new one
            const newChat: ExtendedChat = {
              id: Date.now().toString(),
              title: 'New Chat',
              messages: [],
              createdAt: Date.now(),
              updatedAt: Date.now(),
              sessionId: uuidv4() // Generate a new session ID locally
            };
            setChats([newChat]);
          } else {
            // If appending, combine with existing chats, otherwise replace
            if (append) {
              setChats(prevChats => {
                // Filter out duplicates (in case the API returns the same chat again)
                const newChatIds = new Set(convertedChats.map(c => c.id));
                const filteredPrevChats = prevChats.filter(c => !newChatIds.has(c.id));
                return [...filteredPrevChats, ...convertedChats];
              });
            } else {
              setChats(convertedChats);
            }

            // If we have a current chat, keep it selected
            const currentChatId = currentChat?.id;
            if (currentChatId) {
              const updatedCurrentChat = convertedChats.find(c => c.id === currentChatId);
              if (updatedCurrentChat) {
                setCurrentChat(updatedCurrentChat);
              }
            }
          }
        } catch (error) {
          console.error('Error loading chat sessions from API:', error);
          showToast('Failed to load chats from server.', 'error');

          // Create a new default chat when API fails
          const newChat: ExtendedChat = {
            id: Date.now().toString(),
            title: 'New Chat',
            messages: [],
            createdAt: Date.now(),
            updatedAt: Date.now(),
            sessionId: uuidv4() // Generate a new session ID locally
          };
          setChats([newChat]);
        } finally {
          setIsLoadingSessions(false);
        }
      } else {
        // If not logged in, create a default chat
        const newChat: ExtendedChat = {
          id: Date.now().toString(),
          title: 'New Chat',
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          sessionId: uuidv4() // Generate a new session ID locally
        };
        setChats([newChat]);
      }
    };

    // Initial load of chats when component mounts or user changes
    loadChats();

    // No more refresh interval - removed the cron job

    // Export loadChats so it can be used by other functions
    // @ts-ignore - This is a hack to make loadChats available to other functions
    window.loadChats = (append = false) => loadChats(append);

    return () => {
      // @ts-ignore - Cleanup
      delete window.loadChats;
    };
  }, [user, chatPagination.limit, chatPagination.skip]);

  // Add a separate effect to handle scrolling after chats are loaded or updated
  useEffect(() => {
    if (!isLoadingSessions && currentChat && currentChat.messages.length > 0) {
      // Use a small timeout to ensure DOM has updated
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [isLoadingSessions, currentChat]);

  // Update chat navigation effect to auto-create new chat
  useEffect(() => {
    const path = window.location.pathname;

    // If the user is on the root path or /chat path, create a new chat automatically
    // But only if we haven't already created one for this navigation
    if ((path === '/' || path === '/chat' || path === '/chat/') && !autoCreatedChatRef.current) {
      // Set the flag to prevent repeated creation
      autoCreatedChatRef.current = true;

      // Generate the same behavior as clicking "New Chat" button
      const newChat: ExtendedChat = {
        id: Date.now().toString(),
        title: 'New Chat',
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        sessionId: uuidv4() // Generate a new session ID locally
      };

      setChats(prevChats => [newChat, ...prevChats]);
      setCurrentChat(newChat);

      // Navigate to the new chat route
      navigate(`/chat/${newChat.id}`, { replace: true });
    } else if (chatId && chats.length > 0) {
      // If we're on a specific chat route, load that chat
      const chat = chats.find(c => c.id === chatId);
      if (chat) {
        setCurrentChat(chat);
      } else {
        // If chat not found, redirect to create a new chat
        navigate('/chat');
      }
    }

    // Reset the flag when navigating away from the chat routes
    return () => {
      if (path !== '/' && path !== '/chat' && path !== '/chat/') {
        autoCreatedChatRef.current = false;
      }
    };
  }, [navigate, chats.length, chatId]);

  useEffect(() => {
    // Scroll to bottom whenever messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentChat?.messages]);

  const handleNewChat = () => {
    // Create a new chat with a new UUID
    const newChat: ExtendedChat = {
      id: Date.now().toString(),
      title: 'New Chat',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      sessionId: uuidv4() // Generate a new session ID locally
    };

    setChats([newChat, ...chats]);
    setCurrentChat(newChat);
    navigate(`/chat/${newChat.id}`);
  };

  const handleSelectChat = async (chat: ExtendedChat) => {
    setCurrentChat(chat);
    navigate(`/chat/${chat.id}`);

    // Fetch latest chat data when selecting a chat
    if (user) {
      try {
        // @ts-ignore - Use the loadChats function we attached to window
        if (window.loadChats) {
          // @ts-ignore
          await window.loadChats();
        }
      } catch (error) {
        console.error('Error refreshing chats:', error);
      }
    }
  };

  const handleDeleteChat = async (chatToDelete: ExtendedChat) => {
    // If the chat has a sessionId, attempt to delete it from the API
    if (chatToDelete.sessionId && user) {
      try {
        const success = await deleteChatSession(chatToDelete.sessionId);
        if (success) {
          // Remove from local state
          const updatedChats = chats.filter(chat => chat.id !== chatToDelete.id);
          setChats(updatedChats);

          showToast('Chat deleted successfully', 'success');

          // If the deleted chat was the current one, navigate away
          if (currentChat?.id === chatToDelete.id) {
            setCurrentChat(null);
            navigate('/chat');
          }
        } else {
          showToast('Failed to delete chat from server', 'error');
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
        showToast('Error deleting chat. Please try again.', 'error');
      }
    }
  };

  const handleSubmitPrompt = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((!prompt.trim() && fileAttachments.length === 0) || isGenerating) return;

    // Create a new chat if one doesn't exist
    if (!currentChat) {
      // Generate a new UUID for the session
      const sessionId = uuidv4();

      const newChat: ExtendedChat = {
        id: Date.now().toString(),
        title: prompt.slice(0, 30) + (prompt.length > 30 ? '...' : '') || 'File Upload Chat',
        messages: [],
        createdAt: Date.now(),
        updatedAt: Date.now(),
        sessionId: sessionId
      };

      // Add user message
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: prompt,
        timestamp: Date.now(),
        files: fileAttachments.length > 0 ? fileAttachments.map(file => ({
          name: file.name,
          type: file.type,
          size: file.size
        })) : undefined
      };

      newChat.messages.push(userMessage);

      // Update state first
      setCurrentChat(newChat);
      setChats([newChat, ...chats]);
      setPrompt('');
      setFileAttachments([]);

      // Generate AI response before navigation
      await getAIResponse(newChat, fileAttachments);

      // Navigate after response is generated with replace to prevent back button issues
      // Use replace: true to ensure we don't create a history entry for /chat/ or /chat
      navigate(`/chat/${newChat.id}`, { replace: true });
    } else {
      // Add user message to existing chat
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: prompt,
        timestamp: Date.now(),
        files: fileAttachments.length > 0 ? fileAttachments.map(file => ({
          name: file.name,
          type: file.type,
          size: file.size
        })) : undefined
      };

      const updatedChat = {
        ...currentChat,
        messages: [...currentChat.messages, userMessage],
        updatedAt: Date.now()
      };

      const updatedChats = chats.map(chat =>
        chat.id === updatedChat.id ? updatedChat : chat
      );

      setCurrentChat(updatedChat);
      setChats(updatedChats);
      setPrompt('');
      setFileAttachments([]);

      // Get AI response from API
      await getAIResponse(updatedChat, fileAttachments);
    }
  };

  // Function to show toast notifications
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({
      visible: true,
      message,
      type
    });

    // Auto hide toast after 5 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 5000);
  };

  const getAIResponse = async (chat: ExtendedChat, attachments: File[] = []) => {
    setIsGenerating(true);

    try {
      // Get the last user message
      const lastMessage = chat.messages[chat.messages.length - 1];

      // Ensure the chat has a valid session ID
      if (!chat.sessionId) {
        // Generate a new UUID for the session
        chat.sessionId = uuidv4();
        console.log('Generated new session ID:', chat.sessionId);
      }

      let aiResponseText = '';
      let sources: {
        id: string;
        title: string;
        page: number;
        content: string;
        type: 'pdf' | 'doc' | 'text';
        url?: string;
      }[] = [];

      try {
        // Call the API with the session ID, always use knowledge base, and include file attachments
        const apiResponse = await sendChatMessage(
          lastMessage.content,
          true, // Always use knowledge base
          chat.sessionId,
          attachments // Pass file attachments to API call
        );

        // Extract response text
        aiResponseText = apiResponse.responseText;
        console.log('API Response Text:', aiResponseText);

        // Process sources from finalOutput format to the format expected by our UI
        if (apiResponse.sources && apiResponse.sources.length > 0) {
          console.log('Found sources:', apiResponse.sources);

          // Transform the finalOutput sources to our UI format
          const rawSources = await Promise.all(apiResponse.sources.map(async (source, index) => {
            // Fetch PDF URL for each source
            let url = null;
            if (source.mongodb_id && source.fileType.toLowerCase() === 'pdf') {
              console.log(`Fetching PDF URL for ${source.fileName} (${source.mongodb_id})`);
              url = await fetchPdfBinary(source.mongodb_id);
              console.log(`Got URL: ${url}`);
            }

            return {
              id: source.mongodb_id || `source-${index}`,
              title: source.fileName || `Document ${index + 1}`,
              page: source.page_number || 1,
              content: `Content from ${source.fileName || 'document'}, page ${source.page_number || 1}`,
              type: (source.fileType || 'text').toLowerCase() as 'pdf' | 'doc' | 'text',
              url: url || undefined
            };
          }));

          console.log('This is the raw sources', rawSources);

          // Deduplicate sources based on id and page number
          const uniqueSourceMap = new Map();

          rawSources.forEach(source => {
            const key = `${source.id}-${source.page}`;
            if (!uniqueSourceMap.has(key)) {
              uniqueSourceMap.set(key, source);
            }
          });

          // Convert unique sources back to array
          sources = Array.from(uniqueSourceMap.values());

          console.log('Processed unique sources for UI:', sources);
        }
      } catch (error) {
        console.error('Error getting AI response:', error);
        showToast('Failed to get a response from Agent GG. Please try again.', 'error');
        aiResponseText = "I'm sorry, I encountered an error processing your request. Please try again later.";
      }

      const assistantMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: aiResponseText,
        timestamp: Date.now(),
        sources: sources.length > 0 ? sources : undefined
      };

      console.log('Creating assistant message with sources:', assistantMessage.sources);

      // Update chat with AI response
      const updatedChat = {
        ...chat,
        messages: [...chat.messages, assistantMessage],
        updatedAt: Date.now()
      };

      // If this is the first message, update the chat title
      if (chat.messages.length === 1) {
        updatedChat.title = chat.messages[0].content.slice(0, 30) +
          (chat.messages[0].content.length > 30 ? '...' : '') || 'File Upload Chat';
      }

      // Create a fresh copy of the chats array to ensure React recognizes state change
      const updatedChats = chats.map(c =>
        c.id === updatedChat.id ? updatedChat : c
      );

      // Update state with the new chat data
      setCurrentChat(updatedChat);
      setChats(updatedChats);

    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitPrompt(e);
    }
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);

    // Auto resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 400); // Increased max height
    textarea.style.height = `${newHeight}px`;
  };

  // Add a new function to handle viewing competition alerts
  const handleViewCompetitionAlerts = () => {
    // Don't mark alerts as viewed automatically
    // This allows the alert indicator to remain visible in the sidebar

    // Navigate to competition alerts page
    navigate('/competition-alerts');
  };

  // Add effect to check if competition alerts have been viewed
  useEffect(() => {
    // Always show competition alerts button in the header
    setShowCompetitionAlerts(true);
  }, []);

  const handleRegenerate = async (messageId: string) => {
    if (isGenerating || !currentChat) return;

    // Find the index of the message to be regenerated
    const messageIndex = currentChat.messages.findIndex(msg => msg.id === messageId);

    // If message not found or it's not the last message in the conversation, do nothing
    if (messageIndex === -1 || messageIndex !== currentChat.messages.length - 1) return;

    // Ensure the previous message is a user message
    const previousMessageIndex = messageIndex - 1;
    if (previousMessageIndex < 0 || currentChat.messages[previousMessageIndex].role !== 'user') {
      showToast('Cannot regenerate response: No user message found.', 'error');
      return;
    }

    // Remove the last assistant message (the one that needs regeneration)
    const updatedMessages = [...currentChat.messages];
    updatedMessages.pop();

    // Preserve the session ID when updating the chat
    const updatedChat = {
      ...currentChat,
      messages: updatedMessages,
      updatedAt: Date.now()
    };

    const updatedChats = chats.map(chat =>
      chat.id === updatedChat.id ? updatedChat : chat
    );

    setCurrentChat(updatedChat);
    setChats(updatedChats);

    // Generate a new response using the API
    await getAIResponse(updatedChat, fileAttachments);
  };

  const handleLoadMoreChats = () => {
    setChatPagination(prev => ({
      ...prev,
      skip: prev.skip + prev.limit
    }));

    // Load more chats with append=true
    // @ts-ignore
    window.loadChats(true);
  };

  // Function to request microphone access and start recording
  const startRecording = async () => {
    try {
      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream);

      // Clear previous audio chunks
      audioChunksRef.current = [];

      // Event handler for when data is available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Event handler for when recording stops
      mediaRecorder.onstop = async () => {
        try {
          // Create audio blob from chunks
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

          setIsGenerating(true);
          // Send audio for transcription
          const transcription = await transcribeAudio(audioBlob);

          // Set the transcribed text as the prompt
          setPrompt(prev => prev + transcription);

          // Focus the textarea
          promptInputRef.current?.focus();
        } catch (error) {
          console.error('Transcription error:', error);
          showToast('Failed to transcribe audio. Please try again.', 'error');
        } finally {
          setIsGenerating(false);
        }
      };

      // Start recording
      mediaRecorder.start();
      setAudioRecorder(mediaRecorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      showToast('Could not access microphone. Please check permissions.', 'error');
    }
  };

  // Function to stop recording
  const stopRecording = () => {
    if (audioRecorder && audioRecorder.state !== 'inactive') {
      audioRecorder.stop();
      setIsRecording(false);

      // Release the microphone by stopping all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  // Toggle recording state
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Clean up media streams when component unmounts
  useEffect(() => {
    return () => {
      // Stop any active media streams when component unmounts
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Handler for file selection via input
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFileAttachments(prev => [...prev, ...newFiles]);
    }
  };

  // Handler for file drop
  const handleFileDrop = (e: React.DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      setFileAttachments(prev => [...prev, ...newFiles]);
    }
  };

  // Handle drag enter
  const handleDragEnter = (e: React.DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Handle drag leave
  const handleDragLeave = (e: React.DragEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // Handler for paste events (for images)
  const handlePaste = (e: React.ClipboardEvent) => {
    if (e.clipboardData && e.clipboardData.files.length > 0) {
      const pastedFiles = Array.from(e.clipboardData.files);
      const imageFiles = pastedFiles.filter(file => file.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        e.preventDefault(); // Prevent default paste behavior for images
        setFileAttachments(prev => [...prev, ...imageFiles]);
      }
    }
  };

  // Remove a file from attachments
  const removeFile = (index: number) => {
    setFileAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Trigger file input click
  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex h-screen bg-secondary overflow-hidden">
      {/* Custom style for hiding scrollbar in WebKit browsers */}
      <style>
        {`
        textarea::-webkit-scrollbar {
          display: none;
        }

        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-1px) rotate(-1deg); }
          40% { transform: translateX(1px) rotate(1deg); }
          60% { transform: translateX(-1px) rotate(0deg); }
          80% { transform: translateX(1px) rotate(0deg); }
        }

        @keyframes sparkle {
          0%, 100% { opacity: 0; transform: scale(0); }
          50% { opacity: 1; transform: scale(1); }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(139, 92, 246, 0.5); }
          50% { box-shadow: 0 0 15px rgba(139, 92, 246, 0.8); }
        }

        @keyframes waveText {
          0%, 100% { transform: translateY(0); }
          25% { transform: translateY(-4px); }
          75% { transform: translateY(4px); }
        }

        /* Add styles for the chat message bubbles */
        .chat-message p, .chat-message li, .chat-message code {
          font-size: 0.85rem !important;
        }

        .chat-message pre code {
          font-size: 0.8rem !important;
        }

        .chat-message h1, .chat-message h2, .chat-message h3 {
          font-size: 1rem !important;
        }

        /* Add styles for tables in markdown */
        .chat-message table {
          border-collapse: collapse;
          width: 100%;
          margin: 1rem 0;
          font-size: 0.85rem !important;
        }

        .chat-message th {
          background-color: rgba(30, 41, 59, 0.8);
          color: #ffffff;
          font-weight: 600;
          text-align: left;
          padding: 0.5rem;
          border: 1px solid rgba(100, 116, 139, 0.5);
        }

        .chat-message td {
          padding: 0.5rem;
          border: 1px solid rgba(100, 116, 139, 0.5);
          border-top: none;
        }

        .chat-message tr:nth-child(even) {
          background-color: rgba(30, 41, 59, 0.3);
        }

        .chat-message tr:hover {
          background-color: rgba(30, 41, 59, 0.5);
        }

        /* Style for text in double quotes */
        .chat-message p:not(pre p)::before,
        .chat-message li:not(pre li)::before,
        .chat-message td:not(pre td)::before {
          content: none;
        }

        .chat-message p:not(pre p),
        .chat-message li:not(pre li),
        .chat-message td:not(pre td) {
          position: relative;
        }

        /* Use a regex-like approach with CSS content replacement */
        .chat-message p:not(pre p),
        .chat-message li:not(pre li),
        .chat-message td:not(pre td) {
          color: inherit;
        }

        /* Apply highlight effect to quoted text */
        .chat-message p:not(pre p) strong,
        .chat-message li:not(pre li) strong,
        .chat-message td:not(pre td) strong {
          font-weight: 600;
        }

        /* Add custom styling for quoted text using a content-replacement technique */
        .chat-message span.quoted-text {
          color: #38bdf8;
          font-weight: 500;
          display: inline;
        }

        .wave-text span {
          display: inline-block;
          animation: waveText 1.5s ease infinite;
        }

        .wave-text span:nth-child(2) { animation-delay: 0.1s; }
        .wave-text span:nth-child(3) { animation-delay: 0.2s; }
        .wave-text span:nth-child(4) { animation-delay: 0.3s; }
        .wave-text span:nth-child(5) { animation-delay: 0.4s; }
        .wave-text span:nth-child(6) { animation-delay: 0.5s; }
        .wave-text span:nth-child(7) { animation-delay: 0.6s; }
        .wave-text span:nth-child(8) { animation-delay: 0.7s; }
        .wave-text span:nth-child(9) { animation-delay: 0.8s; }
        .wave-text span:nth-child(10) { animation-delay: 0.9s; }
        .wave-text span:nth-child(11) { animation-delay: 1.0s; }
        .wave-text span:nth-child(12) { animation-delay: 1.1s; }

        .button-border-glow {
          position: relative;
          z-index: 0;
          overflow: hidden;
          background: rgba(30, 41, 59, 0.8);
          border: none !important;
        }

        .button-border-glow::before {
          content: '';
          position: absolute;
          z-index: -2;
          left: -50%;
          top: -150%;
          width: 200%;
          height: 400%;
          opacity: 0.1;
          background-repeat: no-repeat;
          background-size: 50% 50%, 50% 50%;
          background-position: 0 0, 100% 0, 100% 100%, 0 100%;
          background-image: linear-gradient(#3b82f6, #3b82f6), linear-gradient(#10b981, #10b981), linear-gradient(#6366f1, #6366f1), linear-gradient(#8b5cf6, #8b5cf6);
          animation: rotate 3s linear infinite;
        }

        .button-border-glow::after {
          content: '';
          position: absolute;
          z-index: -1;
          left: 2px;
          top: 2px;
          width: calc(100% - 4px);
          height: calc(100% - 4px);
          background: rgba(30, 41, 59, 0.8);
          border-radius: 4px;
        }

        .report-border::before {
          background-image: linear-gradient(#7c3aed, #8b5cf6), linear-gradient(#6366f1, #818cf8), linear-gradient(#5eead4, #2dd4bf), linear-gradient(#7c3aed, #8b5cf6);
        }

        .alert-border::before {
          background-image: linear-gradient(#ef4444, #f87171), linear-gradient(#f59e0b, #fbbf24), linear-gradient(#ef4444, #f87171), linear-gradient(#f59e0b, #fbbf24);
        }

        .task-border::before {
          background-image: linear-gradient(#3b82f6, #60a5fa), linear-gradient(#10b981, #34d399), linear-gradient(#3b82f6, #60a5fa), linear-gradient(#10b981, #34d399);
        }

        .power-border::before {
          background-image: linear-gradient(#f59e0b, #fbbf24), linear-gradient(#ef4444, #f87171), linear-gradient(#f59e0b, #fbbf24), linear-gradient(#ef4444, #f87171);
        }

        .important-border::before {
          background-image: linear-gradient(#8b5cf6, #a78bfa), linear-gradient(#7c3aed, #8b5cf6), linear-gradient(#8b5cf6, #a78bfa), linear-gradient(#7c3aed, #8b5cf6);
        }

        .env-border::before {
          background-image: linear-gradient(#10b981, #34d399), linear-gradient(#059669, #10b981), linear-gradient(#10b981, #34d399), linear-gradient(#059669, #10b981);
        }

        .bias-border::before {
          background-image: linear-gradient(#6366f1, #818cf8), linear-gradient(#4f46e5, #6366f1), linear-gradient(#6366f1, #818cf8), linear-gradient(#4f46e5, #6366f1);
        }

        .report-icon {
          animation: rotate 5s linear infinite;
          transform-origin: center;
        }

        .alert-icon {
          animation: shake 1.5s infinite;
          transform-origin: center;
        }

        .sparkle {
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #fff;
          pointer-events: none;
          z-index: 2;
        }

        .sparkle-orange {
          background: #FFC107;
        }

        .sparkle:nth-child(1) {
          top: -2px;
          left: 0px;
          animation: sparkle 1.5s ease-in-out infinite;
        }

        .sparkle:nth-child(2) {
          top: 5px;
          right: 0px;
          animation: sparkle 1.7s ease-in-out 0.2s infinite;
        }

        .sparkle:nth-child(3) {
          bottom: -3px;
          left: 50%;
          animation: sparkle 1.3s ease-in-out 0.5s infinite;
        }

        .loading-spinner {
          animation: rotate 1s linear infinite;
        }

        .fade-in {
          animation: fadeIn 0.5s ease-in forwards;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        .shimmer-text {
          position: relative;
          color: rgba(139, 92, 246, 0.8);
          background: linear-gradient(
            90deg,
            rgba(139, 92, 246, 0.8) 0%,
            rgba(255, 255, 255, 0.9) 50%,
            rgba(139, 92, 246, 0.8) 100%
          );
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 2s linear infinite;
        }

        .drag-active {
          border: 2px dashed #8b5cf6;
          background-color: rgba(139, 92, 246, 0.1);
        }
        `}
      </style>
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        chats={chats}
        currentChatId={currentChat?.id}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        onNewChat={handleNewChat}
        onLogout={logout}
        userName={user?.name || ''}
        isLoading={isLoadingSessions}
        onLoadMore={handleLoadMoreChats}
        hasMoreChats={chatPagination.hasMore}
      />

      {/* Main Content */}
      <div className="flex flex-col flex-1 h-full overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between px-3 py-2 border-b border-border-color">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="md:hidden p-1.5 rounded-md hover:bg-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <h1 className="text-lg font-semibold text-white">
            {currentChat ? currentChat.title : 'New Chat'}
          </h1>
          <div className="flex items-center gap-1.5">
            {showReportNotification && (
              <button
                onClick={handleViewReport}
                className="button-border-glow report-border flex items-center gap-1.5 text-white py-1.5 px-3 rounded-md text-xs font-medium transition-all"
              >
                <div className="relative">
                  <div className="sparkle"></div>
                  <div className="sparkle"></div>
                  <div className="sparkle"></div>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 report-icon text-accent">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                  </svg>
                </div>
                <span>Today's Report is Available</span>
              </button>
            )}

            {showCompetitionAlerts && (
              <button
                onClick={handleViewCompetitionAlerts}
                className="button-border-glow alert-border flex items-center gap-1.5 text-white py-1.5 px-3 rounded-md text-xs font-medium transition-all"
              >
                <div className="relative">
                  <div className="sparkle sparkle-orange"></div>
                  <div className="sparkle sparkle-orange"></div>
                  <div className="sparkle sparkle-orange"></div>
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border border-white z-10"></div>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 alert-icon text-accent">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
                  </svg>
                </div>
                <span>Competition Alerts</span>
              </button>
            )}
          </div>
        </header>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-0">
          {isLoadingSessions ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="loading-spinner w-10 h-10 border-3 border-gray-300 border-t-accent rounded-full"></div>
              <p className="mt-3 text-sm text-gray-400">Loading your conversations...</p>
            </div>
          ) : !currentChat || currentChat.messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="bg-primary/90 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl border border-white/5 relative max-w-5xl w-full p-5 text-center">
                <h2 className="text-xl font-bold text-white mb-2">Analytics Dashboard</h2>
                <div className="text-sm text-gray-400 mb-5">
                  <p>Our advanced analytics dashboard is currently under development.</p>
                  <p>Stay tuned for powerful insights coming soon!</p>
                </div>
                <div className="inline-block bg-gradient-to-r from-accent to-purple-500 text-white py-1.5 px-5 rounded-full text-xs font-medium shadow-lg animate-pulse">
                  Coming Soon
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto py-6">
              {currentChat.messages.map(message => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onRegenerate={handleRegenerate}
                />
              ))}
              {isGenerating && (
                <div className="py-4 flex items-center space-x-2 fade-in">
                  <div className="h-7 w-7 rounded-full bg-accent flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                      <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                      <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
                    </svg>
                  </div>
                  <div className="shimmer-text font-medium text-xs">
                    {loadingMessages[loadingMessageIndex]}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Form */}
        <div className="px-4 sm:px-0 pb-4 pt-2">
          <div className="max-w-5xl mx-auto">
            {/* Collapsible Buttons Drawer */}
            <div className="relative mb-8">
              <div
                className={`transition-all duration-300 overflow-hidden ${showButtonsDrawer ? 'max-h-96 opacity-100 mb-5' : 'max-h-0 opacity-0'}`}
                style={{ backdropFilter: 'blur(8px)' }}
              >
                <div className="bg-primary/30 border border-white/10 rounded-lg p-3 shadow-xl">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap justify-center gap-2">
                      <button
                        onClick={() => setPrompt("Should I do this now or prioritize something else? \n")}
                        className="button-border-glow task-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Evaluate Tasks
                      </button>
                      <button
                        onClick={() => setPrompt("Give me the list of all my pending clickup tasks (that is in 'to do' status only,  not backlog or any other status and must be my clickup tasks only) and my personal tasks in this tabular format- Task Name, Task Description, Task Due Date, Priority, Your Assumed Priority, Link of the task. Also sort the list based on your assumed priority and also give the reason for your assumed priority. If its not to do status then do not show (not even backlogs/backlog) \n")}
                        className="button-border-glow task-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Get Clickup Tasks
                      </button>
                      <button
                        onClick={() => setPrompt("Give me the list of all my tasks from the calendar")}
                        className="button-border-glow task-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Get Calendar Tasks
                      </button>
                      <button
                        onClick={() => setPrompt("Give me the list of all my tasks from the calendar and from clickup")}
                        className="button-border-glow task-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Get All Tasks
                      </button>
                      <button
                        onClick={() => setPrompt("Mark this task as complete in clickup: \n")}
                        className="button-border-glow task-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Task Complete
                      </button>
                      <button
                        onClick={() => setPrompt("Search this in my company knowledge base: \n")}
                        className="button-border-glow task-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Knowledge Base
                      </button>
                      <button
                        onClick={() => setPrompt("Rewrite this on my own way:\n")}
                        className="button-border-glow important-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Re-write
                      </button>
                      <button
                        onClick={() => setPrompt("Store this in my notes:\n")}
                        className="button-border-glow important-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Take Note
                      </button>

                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                    <button
                        onClick={() => setPrompt("Please retrieve/fetch the note related to this:\n")}
                        className="button-border-glow important-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Retrieve Note
                      </button>
                      <button
                        onClick={() => setPrompt("What is my most important task?")}
                        className="button-border-glow important-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Most Important Task
                      </button>
                      <button
                        onClick={() => setPrompt("Create a clickup task in 'Tech Development' list and assign it to 'Aukik'. Task name: \n")}
                        className="button-border-glow important-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Create Clickup Task
                      </button>
                      <button
                        onClick={() => setPrompt("Analyze the potential power dynamics and impact of this decision\n\nThe Decision:\n\nKey Stakeholders:\n\nYour Primary Goal(s) for this Decision:\n\nKey Relationships Involved:\n")}
                        className="button-border-glow power-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Power Checker
                      </button>
                      <button
                        onClick={() => setPrompt("Check any potential cognitive biases about this decision:\n\nYour Decision or Situation: \nYour Main Reasons & Supporting Information: \nAlternatives You've Considered (if any): \nYour Confidence Level & Prior Investment (Time/Money): \n")}
                        className="button-border-glow bias-border text-white py-1 px-3 rounded-md text-xs transition-all"
                      >
                        Bias Checker
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Toggle Button */}
              <button
                onClick={() => setShowButtonsDrawer(!showButtonsDrawer)}
                className="absolute left-1/2 transform -translate-x-1/2 -bottom-5 z-10 flex items-center justify-center w-28 h-8 text-white text-xs backdrop-blur-md bg-accent/30 border border-accent/30 rounded-full shadow-lg hover:bg-accent/40 transition-all"
              >
                <div className="flex items-center">
                  {showButtonsDrawer ? (
                    <>
                      <span>Close</span>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 ml-1">
                        <path fillRule="evenodd" d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z" clipRule="evenodd" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <span>Templates</span>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 ml-1">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </div>
              </button>
            </div>

            <form
              onSubmit={handleSubmitPrompt}
              className="relative"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleFileDrop}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            >
              {/* File attachments preview */}
              {fileAttachments.length > 0 && (
                <div className="mb-2 p-1.5 border border-border-color rounded-lg bg-primary/20 flex flex-wrap gap-1.5">
                  {fileAttachments.map((file, index) => (
                    <div key={index} className="relative group">
                      <div className="flex items-center p-1.5 bg-primary/40 rounded-md">
                        <div className="flex items-center">
                          {file.type.startsWith('image/') ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="h-10 w-10 object-cover rounded-md mr-2"
                            />
                          ) : (
                            <div className="h-10 w-10 flex items-center justify-center bg-gray-700 rounded-md mr-2">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-gray-300">
                                <path d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z" />
                                <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                              </svg>
                            </div>
                          )}
                          <div className="text-xs text-white truncate max-w-xs">
                            {file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="ml-2 text-gray-400 hover:text-white"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <textarea
                ref={promptInputRef}
                value={prompt}
                onChange={handlePromptChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder="Message Agent GG..."
                className={`w-full bg-input-bg py-3 px-4 pr-28 rounded-lg resize-y min-h-[50px] max-h-80 focus:outline-none focus:ring-1 focus:ring-accent text-sm text-white placeholder-gray-400 border border-border-color ${isDragging ? 'drag-active' : ''}`}
                style={{
                  msOverflowStyle: 'auto',
                  scrollbarWidth: 'auto'
                }}
                rows={3}
                disabled={isGenerating}
              />

              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept="image/*,application/pdf"
                className="hidden"
                disabled={isGenerating}
              />

              {/* File upload button */}
              <button
                type="button"
                onClick={triggerFileUpload}
                disabled={isGenerating}
                className="absolute right-20 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                title="Attach files"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M18.97 3.659a2.25 2.25 0 00-3.182 0l-10.94 10.94a3.75 3.75 0 105.304 5.303l7.693-7.693a.75.75 0 011.06 1.06l-7.693 7.693a5.25 5.25 0 11-7.424-7.424l10.939-10.94a3.75 3.75 0 115.303 5.304L9.097 18.835l-.008.008-.007.007-.002.002-.003.002A2.25 2.25 0 015.91 15.66l7.81-7.81a.75.75 0 011.061 1.06l-7.81 7.81a.75.75 0 001.054 1.068L18.97 6.84a2.25 2.25 0 000-3.182z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Microphone button */}
              <button
                type="button"
                onClick={toggleRecording}
                disabled={isGenerating}
                className={`absolute right-12 top-1/2 -translate-y-1/2 p-1.5 rounded-full ${isRecording ? 'bg-red-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isRecording ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M8.25 4.5a3.75 3.75 0 117.5 0v8.25a3.75 3.75 0 11-7.5 0V4.5z" />
                    <path d="M6 10.5a.75.75 0 01.75.75v1.5a5.25 5.25 0 1010.5 0v-1.5a.75.75 0 011.5 0v1.5a6.751 6.751 0 01-6 6.709v2.291h3a.75.75 0 010 1.5h-7.5a.75.75 0 010-1.5h3v-2.291a6.751 6.751 0 01-6-6.709v-1.5A.75.75 0 016 10.5z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M8.25 4.5a3.75 3.75 0 117.5 0v8.25a3.75 3.75 0 11-7.5 0V4.5z" />
                    <path d="M6 10.5a.75.75 0 01.75.75v1.5a5.25 5.25 0 1010.5 0v-1.5a.75.75 0 011.5 0v1.5a6.751 6.751 0 01-6 6.709v2.291h3a.75.75 0 010 1.5h-7.5a.75.75 0 010-1.5h3v-2.291a6.751 6.751 0 01-6-6.709v-1.5A.75.75 0 016 10.5z" />
                  </svg>
                )}
              </button>
              <button
                type="submit"
                disabled={(!prompt.trim() && fileAttachments.length === 0) || isGenerating}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              </button>
            </form>
            <div className="flex items-center justify-between text-xs mt-1.5">
              <div className="flex items-center space-x-1.5">
                {/* Knowledge base toggle removed */}
              </div>
              <p className="text-xs text-gray-500">
                Attach PDFs and images via the upload button, drag & drop, or paste (Cmd/Ctrl+V). Agent GG may produce inaccurate information.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toast notification */}
      {toast.visible && (
        <div className={`fixed top-4 right-4 z-50 px-3 py-1.5 rounded-md shadow-lg transition-all duration-300 ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        } text-white text-xs`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default Chat;
